import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Eye, Lock } from "lucide-react";

export const ConjugationTemplates = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("marker");
  const [tense, setTense] = useState("presente");
  const [verb, setVerb] = useState("comer");
  const [text, setText] = useState("");
  const [onlySchool, setOnlySchool] = useState(false);
  const [causal, setCausal] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    const qs = kind === "marker" ? `kind=marker&tense=${tense}`
      : kind === "complement" ? `kind=complement&verb=${encodeURIComponent(verb)}`
      : "kind=connector";
    api.get(`/conjugation/templates?${qs}`).then((r) => setData(r.data)).catch(() => {});
  };

  useEffect(load, [kind, tense, verb]);
  useEffect(() => {
    api.get("/conjugation/templates/settings")
      .then((r) => { setOnlySchool(r.data.only_school); setCausal(r.data.causal_connectors); })
      .catch(() => {});
  }, []);

  const mine = useMemo(() => (data?.items || []).filter((i) => i.source === "school"), [data]);
  const system = useMemo(() => (data?.items || []).filter((i) => i.source === "system"), [data]);

  const add = async () => {
    if (text.trim().length < 2) return;
    setBusy(true);
    try {
      await api.post("/conjugation/templates", {
        kind, text: text.trim(),
        tense: kind === "marker" ? tense : "",
        verb: kind === "complement" ? verb : "",
      });
      setText(""); load(); showPreview();
      toast.success(t("cjt_added"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("cjt_error"));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/conjugation/templates/${id}`);
    load(); showPreview();
  };

  const toggleOnly = async (v) => {
    setOnlySchool(v);
    await api.put("/conjugation/templates/settings", { only_school: v, causal_connectors: causal });
    showPreview();
  };

  const toggleCausal = async (v) => {
    setCausal(v);
    await api.put("/conjugation/templates/settings", { only_school: onlySchool, causal_connectors: v });
  };

  const showPreview = () =>
    api.get(`/conjugation/templates/preview?tense=${tense}&verb=${kind === "complement" ? verb : "comer"}`)
      .then((r) => setPreview(r.data)).catch(() => {});

  return (
    <div className="space-y-5" data-testid="conjugation-templates">
      <Card className="p-5 max-w-3xl">
        <h3 className="text-lg font-semibold mb-1">{t("cjt_title")}</h3>
        <p className="text-sm text-slate-500 mb-5">{t("cjt_subtitle")}</p>

        <div className="flex gap-2 mb-5">
          {[
            { key: "marker", label: t("cjt_kind_marker"), hint: t("cjt_kind_marker_hint") },
            { key: "complement", label: t("cjt_kind_complement"), hint: t("cjt_kind_complement_hint") },
            { key: "connector", label: t("cjt_kind_connector"), hint: t("cjt_kind_connector_hint") },
          ].map((k) => (
            <button key={k.key} onClick={() => setKind(k.key)} data-testid={`cjt-kind-${k.key}`}
                    className={`flex-1 text-left rounded-xl border-2 p-3 transition-all ${
                      kind === k.key ? "border-orange-400 bg-orange-50/60"
                        : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className="block font-semibold text-sm text-slate-900">{k.label}</span>
              <span className="block text-xs text-slate-500 mt-0.5">{k.hint}</span>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 items-end mb-4">
          {kind === "marker" ? (
            <div>
              <Label className="text-xs">{t("cj_tense_col")}</Label>
              <Select value={tense} onValueChange={setTense}>
                <SelectTrigger className="w-[230px] mt-1 bg-white" data-testid="cjt-tense">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(data?.tenses || []).map((tn) => (
                    <SelectItem key={tn} value={tn}>{t(`cj_t_${tn}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : kind === "complement" ? (
            <div>
              <Label className="text-xs">{t("cjt_verb")}</Label>
              <Select value={verb} onValueChange={setVerb}>
                <SelectTrigger className="w-[230px] mt-1 bg-white" data-testid="cjt-verb">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {(data?.verbs || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="flex-1 min-w-[240px]">
            <Label className="text-xs">{
              kind === "marker" ? t("cjt_new_marker")
                : kind === "complement" ? t("cjt_new_complement") : t("cjt_new_connector")
            }</Label>
            <Input value={text} onChange={(e) => setText(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") add(); }}
                   placeholder={
                     kind === "marker" ? t("cjt_ph_marker")
                       : kind === "complement" ? t("cjt_ph_complement") : t("cjt_ph_connector")
                   }
                   className="mt-1 bg-white" data-testid="cjt-text" />
          </div>
          <Button onClick={add} disabled={busy || text.trim().length < 2} data-testid="cjt-add-btn">
            <Plus className="h-4 w-4" /> {t("cjt_add")}
          </Button>
        </div>
        {kind === "marker" && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2"
             data-testid="cjt-marker-warning">
            {t("cjt_marker_warning")}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2 py-3 border-t border-slate-100">
          <Switch id="only-school" checked={onlySchool} onCheckedChange={toggleOnly}
                  data-testid="cjt-only-school" />
          <Label htmlFor="only-school" className="text-xs">{t("cjt_only_school")}</Label>
          <span className="text-xs text-slate-400">· {t("cjt_only_school_hint")}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 pb-3">
          <Switch id="causal" checked={causal} onCheckedChange={toggleCausal}
                  data-testid="cjt-causal" />
          <Label htmlFor="causal" className="text-xs">{t("cjt_causal")}</Label>
          <span className="text-xs text-slate-400">· {t("cjt_causal_hint")}</span>
        </div>

        <Button variant="outline" size="sm" onClick={showPreview} data-testid="cjt-preview-btn">
          <Eye className="h-4 w-4" /> {t("cjt_preview")}
        </Button>
        {preview && (
          <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="cjt-preview">
            <p className="text-lg text-slate-800">{preview.filled}</p>
            <p className="text-xs text-slate-500 mt-1">{preview.text}</p>
          </div>
        )}
      </Card>

      <Card className="p-5 max-w-3xl">
        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
          {t("cjt_mine")} <Badge variant="outline">{mine.length}</Badge>
        </h4>
        {mine.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="cjt-mine-empty">{t("cjt_mine_empty")}</p>
        ) : (
          <div className="space-y-1.5">
            {mine.map((i) => (
              <div key={i.id} data-testid={`cjt-row-${i.id}`}
                   className="flex items-center gap-3 text-sm rounded-lg px-3 py-2 bg-orange-50/50 border border-orange-100">
                <span className="text-slate-800">{i.text}</span>
                <button onClick={() => remove(i.id)} data-testid={`cjt-delete-${i.id}`}
                        className="ml-auto text-slate-400 hover:text-rose-600 transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <h4 className="text-sm font-semibold mt-6 mb-3 flex items-center gap-2 text-slate-500">
          <Lock className="h-3.5 w-3.5" /> {t("cjt_system")} <Badge variant="outline">{system.length}</Badge>
        </h4>
        <div className="flex flex-wrap gap-2">
          {system.map((i, n) => (
            <span key={n} className={`text-xs rounded px-2 py-1 border ${
              onlySchool && mine.length > 0
                ? "bg-slate-50 border-slate-200 text-slate-400 line-through"
                : "bg-white border-slate-200 text-slate-600"}`}>
              {i.text}
            </span>
          ))}
        </div>
      </Card>
    </div>
  );
};
